import ControlPanelController from "./control_panel_controller"
import Sortable from "sortablejs"

const KEBAB_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>'
const DRAG_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>'

export default class extends ControlPanelController {
  static values = {
    companyId: String,
    activeTab: String,
    unitCompany: Boolean,
    descCompany: Boolean,
    hasBelowSov: Boolean
  }

  static targets = [
    "subRetainage",
    // SOV
    "customSovToggle", "sovImportModal", "sovImportFile", "sovImportButton", "sovSearch", "sovCategoriesContainer",
    "sovLineItemModal", "sovLineItemModalTitle", "sovLineItemId", "sovLineItemCategoryId",
    "sovItem", "sovCsiDiv", "sovType", "sovDescription", "sovDefinition", "sovUom", "sovLaborMile", "sovEstFormula",
    "sovCategoryModal", "sovCategoryModalTitle", "sovCategoryId", "sovCategoryName",
    // Fee Schedule
    "customFeeToggle", "feeSearch", "feeCategoriesContainer",
    "feeCategoryModal", "feeCategoryModalTitle", "feeCategoryId", "feeCategoryName",
    "feeLineItemModal", "feeLineItemModalTitle", "feeLineItemId", "feeLineItemCategoryId",
    "feeItem", "feeWork",
    // Payment Requests
    "deptName", "officeName", "buyerName", "buyerAddress", "buyerCityStateZip",
    "buyerNameCa", "buyerAddressCa", "buyerCityStateZipCa", "customInvoiceNumber", "requirePrAttachment",
    // Legal Text
    "legalTextModal", "legalTextModalTitle", "legalTextField", "legalTextAction", "legalTextArea",
    // Approval
    "multipleOwners", "ownersAddPaidFinances", "ownersAddPaidFinancesContractor",
    "ownersAddCcds", "pastFinanceCoverPage", "ownerSignWithoutConsultant",
    "consultantsSeparateCurrency", "contractorsSeparateCurrency",
    // Dashboard (Fund management)
    "fundManagement", "fundIndexLabel", "fundDfcmLabel", "fundProjectLabel",
    // Retention
    "retentionModal", "retentionSection", "defaultRetentionInput",
    "exceptionRuleModal", "exceptionRuleModalTitle", "exceptionRuleId",
    "exceptionRuleType", "exceptionBelowSovAmount", "exceptionState",
    "exceptionRetentionPercentage", "belowSovAmountField", "stateField", "retentionLabel",
    // Delete Confirmation
    "deleteConfirmModal", "deleteConfirmId", "deleteConfirmType", "deleteConfirmMessage", "customSignatures"
  ]

  connect() {
    super.connect()
    this._initSortables()
    this._onClickOutside = () => this._closeAllMenus()
    document.addEventListener("click", this._onClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  // ── Kebab Menus ──────────────────────────────────────────────────

  // S3-3: the company's default retainage on new sub contracts.
  async saveSubRetainage(event) {
    event?.preventDefault()
    const btn = this.element.querySelector("[data-action*='saveSubRetainage'][data-url]")
    const input = this.subRetainageTarget
    try {
      const resp = await fetch(btn.dataset.url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body: JSON.stringify({ default_sub_retainage_pct: input.value })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      const pct = data.default_sub_retainage_pct
      input.value = pct == null ? "" : String(pct).replace(/\.0+$/, "")
      this._flash(pct == null ? "No default retainage" : `New sub contracts start at ${input.value}% retainage`)
    } catch (e) {
      this._flash(e.message, "error")
    }
  }

  _flash(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "error" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }

  toggleMenu(event) {
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const wasHidden = menu.classList.contains("hidden")
    this._closeAllMenus()
    if (wasHidden) menu.classList.remove("hidden")
  }

  _closeAllMenus() {
    this.element.querySelectorAll(".relative > div.absolute").forEach(m => m.classList.add("hidden"))
  }

  // ── Search ────────────────────────────────────────────────────────

  searchSov() {
    const query = this.sovSearchTarget.value.toLowerCase()
    if (!this.hasSovCategoriesContainerTarget) return
    this.sovCategoriesContainerTarget.querySelectorAll("[data-sov-category-block]").forEach(block => {
      const name = block.dataset.categoryName || ""
      block.classList.toggle("hidden", query !== "" && !name.includes(query))
    })
  }

  searchFee() {
    const query = this.feeSearchTarget.value.toLowerCase()
    if (!this.hasFeeCategoriesContainerTarget) return
    this.feeCategoriesContainerTarget.querySelectorAll("[data-fee-category-block]").forEach(block => {
      const name = block.dataset.categoryName || ""
      block.classList.toggle("hidden", query !== "" && !name.includes(query))
    })
  }

  // ── Drag & Drop ───────────────────────────────────────────────────

  _initSortables() {
    if (this._sortableInstances) {
      this._sortableInstances.forEach(s => s.destroy())
    }
    this._sortableInstances = []
    // SOV tbodies
    if (this.hasSovCategoriesContainerTarget) {
      this.sovCategoriesContainerTarget.querySelectorAll("[data-sov-category-id]").forEach(tbody => {
        this._sortableInstances.push(Sortable.create(tbody, {
          handle: ".drag-handle",
          animation: 150,
          ghostClass: "opacity-30",
          onEnd: () => this._onSovSortEnd(tbody)
        }))
      })
    }
    // Fee Schedule tbodies
    if (this.hasFeeCategoriesContainerTarget) {
      this.feeCategoriesContainerTarget.querySelectorAll("[data-fee-category-id]").forEach(tbody => {
        this._sortableInstances.push(Sortable.create(tbody, {
          handle: ".drag-handle",
          animation: 150,
          ghostClass: "opacity-30",
          onEnd: () => this._onFeeSortEnd(tbody)
        }))
      })
    }
  }

  async _onSovSortEnd(tbody) {
    const categoryId = tbody.dataset.sovCategoryId
    const sequences = []
    tbody.querySelectorAll("[data-line-item-id]").forEach((row, index) => {
      sequences.push([row.dataset.lineItemId, index + 1, categoryId])
    })

    try {
      const resp = await fetch(this._financesCollectionUrl("update_sov_sequence"), {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ new_sequence: JSON.stringify(sequences) })
      })
      if (resp.ok) {
        this._showToast("Order updated")
      } else {
        this._showToast("Error updating order", "error")
        await this._loadSovCategories()
      }
    } catch {
      this._showToast("Error updating order", "error")
      await this._loadSovCategories()
    }
  }

  async _onFeeSortEnd(tbody) {
    const categoryId = tbody.dataset.feeCategoryId
    const sequences = []
    tbody.querySelectorAll("[data-line-item-id]").forEach((row, index) => {
      sequences.push([row.dataset.lineItemId, index + 1, categoryId])
    })

    try {
      const resp = await fetch(this._financesCollectionUrl("update_fee_schedule_sequence"), {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ new_sequence: JSON.stringify(sequences) })
      })
      if (resp.ok) {
        this._showToast("Order updated")
      } else {
        this._showToast("Error updating order", "error")
        await this._loadFeeCategories()
      }
    } catch {
      this._showToast("Error updating order", "error")
      await this._loadFeeCategories()
    }
  }

  // ── SOV Categories ──────────────────────────────────────────────────

  addSovCategory() {
    this.sovCategoryModalTitleTarget.textContent = "Add Category"
    this.sovCategoryIdTarget.value = ""
    this.sovCategoryNameTarget.value = ""
    this.sovCategoryModalTarget.querySelector("[data-action*='submitSovCategory']").textContent = "Create"
    this.sovCategoryModalTarget.classList.remove("hidden")
  }

  renameSovCategory(event) {
    this._closeAllMenus()
    const id = event.currentTarget.dataset.id
    const currentName = event.currentTarget.dataset.name
    this.sovCategoryModalTitleTarget.textContent = "Rename Category"
    this.sovCategoryIdTarget.value = id
    this.sovCategoryNameTarget.value = currentName
    this.sovCategoryModalTarget.querySelector("[data-action*='submitSovCategory']").textContent = "Save"
    this.sovCategoryModalTarget.classList.remove("hidden")
  }

  closeSovCategoryModal() {
    this.sovCategoryModalTarget.classList.add("hidden")
  }

  async submitSovCategory() {
    const name = this.sovCategoryNameTarget.value.trim()
    if (!name) return

    const id = this.sovCategoryIdTarget.value
    const isRename = !!id

    const url = this._financesCollectionUrl(isRename ? "rename_sov_category" : "create_sov_category")
    const body = isRename ? { name, sov_category_id: id } : { name, company_id: this.companyIdValue }

    try {
      const resp = await fetch(url, {
        method: isRename ? "PUT" : "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeSovCategoryModal()
        this._showToast(data.message || (isRename ? "Renamed" : "Category added"))
        await this._loadSovCategories()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  deleteSovCategory(event) {
    this._closeAllMenus()
    this._openDeleteConfirm(event.currentTarget.dataset.id, "sovCategory", "Delete this category and all its line items?")
  }

  // ── SOV Line Items ──────────────────────────────────────────────────

  openAddSovLineItem(event) {
    const categoryId = event.currentTarget.dataset.categoryId
    if (this.hasSovLineItemModalTarget) {
      this.sovLineItemModalTitleTarget.textContent = "Add Line Item"
      this.sovLineItemIdTarget.value = ""
      this.sovLineItemCategoryIdTarget.value = categoryId
      this._clearSovLineItemForm()
      this.sovLineItemModalTarget.classList.remove("hidden")
    }
  }

  editSovLineItem(event) {
    const btn = event.currentTarget
    if (this.hasSovLineItemModalTarget) {
      this.sovLineItemModalTitleTarget.textContent = "Edit Line Item"
      this.sovLineItemIdTarget.value = btn.dataset.id
      this.sovLineItemCategoryIdTarget.value = ""
      if (this.hasSovItemTarget) this.sovItemTarget.value = btn.dataset.item || ""
      if (this.hasSovCsiDivTarget) this.sovCsiDivTarget.value = btn.dataset.csiDiv || ""
      if (this.hasSovTypeTarget) this.sovTypeTarget.value = btn.dataset.type || ""
      this.sovDescriptionTarget.value = btn.dataset.description || ""
      this.sovDefinitionTarget.value = btn.dataset.definition || ""
      this.sovUomTarget.value = btn.dataset.uom || ""
      if (this.hasSovLaborMileTarget) this.sovLaborMileTarget.value = btn.dataset.laborMile || ""
      if (this.hasSovEstFormulaTarget) this.sovEstFormulaTarget.value = btn.dataset.estQuantityFormula || ""
      this.sovLineItemModalTarget.classList.remove("hidden")
    }
  }

  closeSovLineItemModal() {
    if (this.hasSovLineItemModalTarget) {
      this.sovLineItemModalTarget.classList.add("hidden")
    }
  }

  async saveSovLineItem() {
    const id = this.sovLineItemIdTarget.value
    const isEdit = !!id

    const body = {
      item: this.hasSovItemTarget ? this.sovItemTarget.value : "",
      csi_div: this.hasSovCsiDivTarget ? this.sovCsiDivTarget.value : "",
      type: this.hasSovTypeTarget ? this.sovTypeTarget.value : "",
      description: this.sovDescriptionTarget.value,
      definition: this.sovDefinitionTarget.value,
      uom: this.sovUomTarget.value,
      labor_mile: this.hasSovLaborMileTarget ? this.sovLaborMileTarget.value : "",
      est_quantity_formula: this.hasSovEstFormulaTarget ? this.sovEstFormulaTarget.value : ""
    }

    if (isEdit) {
      body.id = id
    } else {
      body.sov_category_id = this.sovLineItemCategoryIdTarget.value
    }

    const url = isEdit
      ? this._financesCollectionUrl("edit_line_item")
      : this._financesCollectionUrl("new_line_item")

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeSovLineItemModal()
        this._showToast(data.message || "Saved")
        await this._loadSovCategories()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  deleteSovLineItem(event) {
    this._closeAllMenus()
    this._openDeleteConfirm(event.currentTarget.dataset.id, "sovLineItem", "Delete this line item?")
  }

  async toggleCustomSovLineItems() {
    const checkbox = this.customSovToggleTarget
    const newStatus = checkbox.checked

    try {
      const resp = await fetch(this._financesCollectionUrl("change_custom_sov_line_item_status"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ status: newStatus })
      })
      if (resp.ok) {
        this._showToast("Setting updated")
      } else {
        checkbox.checked = !newStatus
        this._showToast("Error updating setting", "error")
      }
    } catch {
      checkbox.checked = !newStatus
      this._showToast("Network error", "error")
    }
  }

  openSovImportModal() {
    if (this.hasSovImportModalTarget) {
      this.sovImportFileTarget.value = ""
      this.sovImportButtonTarget.disabled = true
      this.sovImportModalTarget.classList.remove("hidden")
    }
  }

  closeSovImportModal() {
    if (this.hasSovImportModalTarget) {
      this.sovImportModalTarget.classList.add("hidden")
    }
  }

  onSovImportFileChange() {
    if (this.hasSovImportButtonTarget) {
      this.sovImportButtonTarget.disabled = !this.sovImportFileTarget.files[0]
    }
  }

  async importSov() {
    const fileInput = this.sovImportFileTarget
    if (!fileInput.files[0]) return

    this.sovImportButtonTarget.disabled = true
    this.sovImportButtonTarget.textContent = "Importing..."

    const formData = new FormData()
    formData.append("file", fileInput.files[0])
    formData.append("company_id", this.companyIdValue)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this._financesCollectionUrl("import_csv"), {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeSovImportModal()
        this._showToast(data.message || "Imported successfully")
        await this._loadSovCategories()
      } else {
        this.sovImportButtonTarget.disabled = false
        this.sovImportButtonTarget.textContent = "Import"
        this._showToast(data.error || "Import failed", "error")
      }
    } catch {
      this.sovImportButtonTarget.disabled = false
      this.sovImportButtonTarget.textContent = "Import"
      this._showToast("Network error", "error")
    }
  }

  // ── Fee Schedule Categories ─────────────────────────────────────────

  addFeeCategory() {
    this.feeCategoryModalTitleTarget.textContent = "Add Category"
    this.feeCategoryIdTarget.value = ""
    this.feeCategoryNameTarget.value = ""
    this.feeCategoryModalTarget.querySelector("[data-action*='submitFeeCategory']").textContent = "Create"
    this.feeCategoryModalTarget.classList.remove("hidden")
  }

  renameFeeCategory(event) {
    this._closeAllMenus()
    const id = event.currentTarget.dataset.id
    const currentName = event.currentTarget.dataset.name
    this.feeCategoryModalTitleTarget.textContent = "Rename Category"
    this.feeCategoryIdTarget.value = id
    this.feeCategoryNameTarget.value = currentName
    this.feeCategoryModalTarget.querySelector("[data-action*='submitFeeCategory']").textContent = "Save"
    this.feeCategoryModalTarget.classList.remove("hidden")
  }

  closeFeeCategoryModal() {
    this.feeCategoryModalTarget.classList.add("hidden")
  }

  async submitFeeCategory() {
    const name = this.feeCategoryNameTarget.value.trim()
    if (!name) return

    const id = this.feeCategoryIdTarget.value
    const isRename = !!id

    const url = this._financesCollectionUrl(isRename ? "rename_fee_schedule_category" : "create_fee_schedule_category")
    const body = isRename ? { name, category_id: id } : { name, company_id: this.companyIdValue }

    try {
      const resp = await fetch(url, {
        method: isRename ? "PUT" : "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeFeeCategoryModal()
        this._showToast(data.message || (isRename ? "Renamed" : "Category added"))
        await this._loadFeeCategories()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  deleteFeeCategory(event) {
    this._closeAllMenus()
    this._openDeleteConfirm(event.currentTarget.dataset.id, "feeCategory", "Delete this category and all its line items?")
  }

  // ── Fee Schedule Line Items ─────────────────────────────────────────

  openAddFeeLineItem(event) {
    const categoryId = event.currentTarget.dataset.categoryId
    if (this.hasFeeLineItemModalTarget) {
      const block = event.currentTarget.closest("[data-fee-category-block]")
      const catName = block?.querySelector("h4")?.textContent || ""
      this.feeLineItemModalTitleTarget.textContent = catName ? `Add Line Item to ${catName}` : "Add Line Item"
      this.feeLineItemIdTarget.value = ""
      this.feeLineItemCategoryIdTarget.value = categoryId
      this.feeItemTarget.value = ""
      this.feeWorkTarget.value = ""
      this.feeLineItemModalTarget.classList.remove("hidden")
    }
  }

  editFeeLineItem(event) {
    const btn = event.currentTarget
    if (this.hasFeeLineItemModalTarget) {
      this.feeLineItemModalTitleTarget.textContent = "Edit Line Item"
      this.feeLineItemIdTarget.value = btn.dataset.id
      this.feeLineItemCategoryIdTarget.value = ""
      this.feeItemTarget.value = btn.dataset.item || ""
      this.feeWorkTarget.value = btn.dataset.work || ""
      this.feeLineItemModalTarget.classList.remove("hidden")
    }
  }

  closeFeeLineItemModal() {
    if (this.hasFeeLineItemModalTarget) {
      this.feeLineItemModalTarget.classList.add("hidden")
    }
  }

  async saveFeeLineItem() {
    const item = this.feeItemTarget.value.trim()
    if (!item) {
      this._showToast("Item is required", "error")
      return
    }

    const id = this.feeLineItemIdTarget.value
    const isEdit = !!id

    const body = {
      item: item,
      work: this.feeWorkTarget.value
    }

    if (isEdit) {
      body.id = id
    } else {
      body.category_id = this.feeLineItemCategoryIdTarget.value
    }

    const url = isEdit
      ? this._financesCollectionUrl("edit_fee_line_item")
      : this._financesCollectionUrl("new_fee_line_item")

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeFeeLineItemModal()
        this._showToast(data.message || "Saved")
        await this._loadFeeCategories()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  deleteFeeLineItem(event) {
    this._closeAllMenus()
    this._openDeleteConfirm(event.currentTarget.dataset.id, "feeLineItem", "Delete this line item?")
  }

  async toggleCustomFeeLineItems() {
    const checkbox = this.customFeeToggleTarget
    const newStatus = checkbox.checked

    try {
      const resp = await fetch(this._financesCollectionUrl("change_custom_fee_schedule_line_item_status"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ status: newStatus })
      })
      if (resp.ok) {
        this._showToast("Setting updated")
      } else {
        checkbox.checked = !newStatus
        this._showToast("Error updating setting", "error")
      }
    } catch {
      checkbox.checked = !newStatus
      this._showToast("Network error", "error")
    }
  }

  // ── Payment Requests ────────────────────────────────────────────────

  async savePaymentRequestsSettings() {
    const body = {
      dept_name: this.deptNameTarget.value,
      office_name: this.officeNameTarget.value,
      invoice_header_buyer_name: this.buyerNameTarget.value,
      invoice_header_buyer_address: this.buyerAddressTarget.value,
      invoice_header_buyer_city_state_zip: this.buyerCityStateZipTarget.value,
      invoice_header_buyer_name_ca: this.hasBuyerNameCaTarget ? this.buyerNameCaTarget.value : "",
      invoice_header_buyer_address_ca: this.hasBuyerAddressCaTarget ? this.buyerAddressCaTarget.value : "",
      invoice_header_buyer_city_state_zip_ca: this.hasBuyerCityStateZipCaTarget ? this.buyerCityStateZipCaTarget.value : "",
      custom_invoice_number: this.customInvoiceNumberTarget.checked ? "true" : "false",
      require_pr_attachment: this.requirePrAttachmentTarget.checked ? "true" : "false"
    }

    try {
      const resp = await fetch(this._financesCollectionUrl("save_payment_requests_settings"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this._showToast(data.message || "Saved")
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Legal Text ──────────────────────────────────────────────────────

  editLegalText(event) {
    const btn = event.currentTarget
    const field = btn.dataset.field
    const label = btn.dataset.label
    const updateAction = btn.dataset.updateAction

    // Find the current text from the card container
    const container = btn.closest("[data-legal-card]")
    const textDiv = container.querySelector(".legal-text-content")
    const currentText = textDiv ? textDiv.textContent.trim() : ""

    this.legalTextModalTitleTarget.textContent = label
    this.legalTextFieldTarget.value = field
    this.legalTextActionTarget.value = updateAction
    this.legalTextAreaTarget.value = currentText
    this.legalTextModalTarget.classList.remove("hidden")
  }

  closeLegalTextModal() {
    if (this.hasLegalTextModalTarget) {
      this.legalTextModalTarget.classList.add("hidden")
    }
  }

  async saveLegalText() {
    const field = this.legalTextFieldTarget.value
    const text = this.legalTextAreaTarget.value
    const action = this.legalTextActionTarget.value

    const endpointMap = {
      updateLegalText: "update_legal_text",
      updateFaLegalText: "update_fa_legal_text",
      updatePcoLegalText: "update_pco_legal_text",
      updateCcdLegalText: "update_ccd_legal_text"
    }
    const endpoint = endpointMap[action] || "update_legal_text"

    try {
      const resp = await fetch(this._financesCollectionUrl(endpoint), {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ field, text, company_id: this.companyIdValue })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeLegalTextModal()
        this._showToast(data.message || "Saved")
        // Surgical DOM update: find the card for this field and update in-place
        const editBtn = this.element.querySelector(`[data-action*="editLegalText"][data-field="${field}"]`)
        if (editBtn) {
          const card = editBtn.closest("[data-legal-card]")
          const textDiv = card.querySelector(".legal-text-content")
          if (textDiv) textDiv.textContent = text
          // Update "Last updated by" info
          const header = card?.querySelector(".bg-gray-50")
          if (header) {
            let metaSpan = header.querySelector(".text-gray-400")
            if (!metaSpan) {
              metaSpan = document.createElement("span")
              metaSpan.className = "text-[13px] text-gray-400 ml-2"
              const labelDiv = header.querySelector("div")
              if (labelDiv) labelDiv.appendChild(metaSpan)
            }
            if (metaSpan) metaSpan.textContent = `Last updated just now`
          }
        }
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Approval Settings ───────────────────────────────────────────────

  async saveApprovalSettings() {
    const body = {
      multiple_owners: this.multipleOwnersTarget.checked ? "yes" : "no",
      owners_add_paid_finances: this.ownersAddPaidFinancesTarget.checked ? "yes" : "no",
      owners_add_paid_finances_contractor: this.ownersAddPaidFinancesContractorTarget.checked ? "yes" : "no",
      owners_add_ccds: this.hasOwnersAddCcdsTarget && this.ownersAddCcdsTarget.checked ? "yes" : "no",
      past_finance_cover_page: this.hasPastFinanceCoverPageTarget && this.pastFinanceCoverPageTarget.checked ? "yes" : "no",
      owner_sign_without_consultant: this.hasOwnerSignWithoutConsultantTarget && this.ownerSignWithoutConsultantTarget.checked ? "yes" : "no",
      consultants_separate_currency: this.consultantsSeparateCurrencyTarget.checked ? "yes" : "no",
      contractors_separate_currency: this.contractorsSeparateCurrencyTarget.checked ? "yes" : "no",
      custom_signatures: this.hasCustomSignaturesTarget && this.customSignaturesTarget.checked ? "yes" : "no",
      company_id: this.companyIdValue
    }

    try {
      const resp = await fetch(this._financesCollectionUrl("update_approval_settings"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this._showToast(data.message || "Saved")
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Dashboard (Fund management, Phelipe 2026-09-29) ─────────────────

  async saveDashboardSettings() {
    if (!this.hasFundManagementTarget) return
    const body = {
      fund_management: this.fundManagementTarget.checked ? "yes" : "no",
      company_id: this.companyIdValue
    }
    // The two project numbers' names; an empty box hides that field.
    if (this.hasFundIndexLabelTarget) body.index_label = this.fundIndexLabelTarget.value
    if (this.hasFundDfcmLabelTarget) body.dfcm_label = this.fundDfcmLabelTarget.value
    // "Project #" under the company's own name; empty goes back to "Project #".
    if (this.hasFundProjectLabelTarget) body.project_number_label = this.fundProjectLabelTarget.value

    try {
      const resp = await fetch(this._financesCollectionUrl("update_dashboard_settings"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this._showToast(data.message || "Saved")
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Retention ───────────────────────────────────────────────────────

  editDefaultRetention() {
    if (this.hasRetentionModalTarget) {
      this.retentionModalTarget.classList.remove("hidden")
    }
  }

  closeRetentionModal() {
    if (this.hasRetentionModalTarget) {
      this.retentionModalTarget.classList.add("hidden")
    }
  }

  async saveDefaultRetention() {
    const percentage = this.defaultRetentionInputTarget.value

    try {
      const resp = await fetch(this._financesCollectionUrl("update_default_retention_percentage"), {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ retention_percentage: percentage })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeRetentionModal()
        this._showToast(data.message || "Saved")
        await this._loadRetentionData()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }


  // ── Exception Rules ─────────────────────────────────────────────────

  openAddExceptionRule() {
    if (this.hasExceptionRuleModalTarget) {
      this.exceptionRuleModalTitleTarget.textContent = "Add Exception Rule"
      this.exceptionRuleIdTarget.value = ""

      // Hide "Below SOV Amount" option if one already exists
      const select = this.exceptionRuleTypeTarget
      const belowOpt = select.querySelector('option[value="below_sov_amount"]')
      if (belowOpt) belowOpt.hidden = this.hasBelowSovValue

      // Default to the first visible option
      if (this.hasBelowSovValue) {
        select.value = "default_state"
      } else {
        select.value = "below_sov_amount"
      }

      this.exceptionBelowSovAmountTarget.value = ""
      if (this.hasExceptionStateTarget) this.exceptionStateTarget.value = ""
      this.exceptionRetentionPercentageTarget.value = ""
      this.toggleExceptionRuleFields()
      this.exceptionRuleModalTarget.classList.remove("hidden")
    }
  }

  async editExceptionRule(event) {
    const id = event.currentTarget.dataset.id

    try {
      const url = new URL(this._financesCollectionUrl("edit_exception_rules_settings"), window.location.origin)
      url.searchParams.set("id", id)

      const resp = await fetch(url, { headers: this._headers() })
      const data = await resp.json()
      const rule = data.exception_rules_setting

      // Show both rule type options when editing (the rule already exists)
      const belowOpt = this.exceptionRuleTypeTarget.querySelector('option[value="below_sov_amount"]')
      if (belowOpt) belowOpt.hidden = false

      this.exceptionRuleModalTitleTarget.textContent = "Edit Exception Rule"
      this.exceptionRuleIdTarget.value = rule.id
      this.exceptionRuleTypeTarget.value = rule.rule_type
      this.exceptionBelowSovAmountTarget.value = rule.below_sov_amount || ""

      // Rebuild state dropdown from server response
      if (this.hasExceptionStateTarget && data.states_with_codes) {
        const stateSelect = this.exceptionStateTarget
        stateSelect.innerHTML = '<option value="">Select a state</option>'
        data.states_with_codes.forEach(s => {
          const opt = document.createElement("option")
          opt.value = s.value
          opt.textContent = s.text
          stateSelect.appendChild(opt)
        })
        stateSelect.value = rule.state || ""
      }

      this.exceptionRetentionPercentageTarget.value = rule.retention_percentage || ""
      this.toggleExceptionRuleFields()
      this.exceptionRuleModalTarget.classList.remove("hidden")
    } catch {
      this._showToast("Error loading rule", "error")
    }
  }

  closeExceptionRuleModal() {
    if (this.hasExceptionRuleModalTarget) {
      this.exceptionRuleModalTarget.classList.add("hidden")
    }
  }

  toggleExceptionRuleFields() {
    const ruleType = this.exceptionRuleTypeTarget.value
    if (this.hasBelowSovAmountFieldTarget) {
      this.belowSovAmountFieldTarget.classList.toggle("hidden", ruleType !== "below_sov_amount")
    }
    if (this.hasStateFieldTarget) {
      this.stateFieldTarget.classList.toggle("hidden", ruleType !== "default_state")
    }
    if (this.hasRetentionLabelTarget) {
      this.retentionLabelTarget.textContent = ruleType === "below_sov_amount"
        ? "Then Change Project Retention % to"
        : "Then Set Project Default Retention % to"
    }
  }

  async saveExceptionRule() {
    const id = this.exceptionRuleIdTarget.value
    const isEdit = !!id

    const ruleParams = {
      rule_type: this.exceptionRuleTypeTarget.value,
      below_sov_amount: this.exceptionBelowSovAmountTarget.value,
      state: this.hasExceptionStateTarget ? this.exceptionStateTarget.value : "",
      retention_percentage: this.exceptionRetentionPercentageTarget.value
    }

    let url, method
    const body = { exception_rules_setting: ruleParams }
    if (isEdit) {
      url = this._financesCollectionUrl("update_exception_rules_settings")
      method = "PUT"
      body.id = id
    } else {
      url = this._financesCollectionUrl("create_exception_rules_settings")
      method = "POST"
    }

    try {
      const resp = await fetch(url, {
        method,
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.closeExceptionRuleModal()
        this._showToast(data.message || "Saved")
        await this._loadRetentionData()
      } else {
        this._showToast(data.errors?.join(", ") || data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  deleteExceptionRule(event) {
    this._openDeleteConfirm(event.currentTarget.dataset.id, "exceptionRule", "Delete this exception rule?")
  }

  // ── Delete Confirmation Modal ────────────────────────────────────

  _openDeleteConfirm(id, type, message) {
    if (!this.hasDeleteConfirmModalTarget) return
    this.deleteConfirmIdTarget.value = id
    this.deleteConfirmTypeTarget.value = type
    this.deleteConfirmMessageTarget.textContent = message
    this.deleteConfirmModalTarget.classList.remove("hidden")
  }

  closeDeleteConfirmModal() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.add("hidden")
    }
  }

  async confirmDelete() {
    const id = this.deleteConfirmIdTarget.value
    const type = this.deleteConfirmTypeTarget.value
    this.closeDeleteConfirmModal()

    const config = {
      sovCategory:    { url: "destroy_sov_category",          reload: () => this._loadSovCategories() },
      sovLineItem:    { url: "destroy_line_item",             reload: () => this._loadSovCategories() },
      feeCategory:    { url: "destroy_fee_schedule_category", reload: () => this._loadFeeCategories() },
      feeLineItem:    { url: "destroy_fee_line_item",         reload: () => this._loadFeeCategories() },
      exceptionRule:  { url: "delete_exception_rules_settings", reload: () => this._loadRetentionData() }
    }[type]

    if (!config) return

    try {
      const resp = await fetch(this._financesCollectionUrl(config.url), {
        method: "DELETE",
        headers: this._headers(),
        body: JSON.stringify({ id })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this._showToast(data.message || "Deleted")
        await config.reload()
      } else {
        this._showToast(data.error || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Client-Side Rendering: SOV ────────────────────────────────────

  async _loadSovCategories() {
    if (!this.hasSovCategoriesContainerTarget) return
    try {
      const resp = await fetch(this._financesCollectionUrl("sov_data"), { headers: this._headers() })
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`)
      const { categories } = await resp.json()
      this.sovCategoriesContainerTarget.innerHTML = categories.map(c => this._renderSovCategory(c)).join("")
      this._initSortables()
    } catch {
      this._showToast("Failed to refresh list", "error")
    }
  }

  _renderSovCategory(category) {
    const esc = this._escHtml.bind(this)
    const nameDown = esc((category.name || "").toLowerCase())
    const lineItems = category.line_items || []

    let columnHeaders = ""
    let rows = ""

    if (lineItems.length > 0) {
      if (this.unitCompanyValue) {
        columnHeaders = `
          <div class="w-8"></div>
          <div class="flex-1 min-w-0 px-2">Unit Name</div>
          <div class="flex-1 min-w-0 px-2 hidden md:block">Description</div>
          <div class="w-28 px-2 hidden md:block">UoM</div>
          <div class="w-24 px-2 hidden lg:block">Labor/Mile</div>
          <div class="flex-1 min-w-0 px-2 hidden lg:block">Est. Quantities Formula</div>`
      } else if (this.descCompanyValue) {
        columnHeaders = `
          <div class="w-8"></div>
          <div class="w-16 px-2">Item</div>
          <div class="w-20 px-2 hidden md:block">CSI Div</div>
          <div class="w-20 px-2 hidden md:block">Type</div>
          <div class="flex-1 min-w-0 px-2">Description Of Work</div>
          <div class="flex-1 min-w-0 px-2 hidden lg:block">Definition</div>
          <div class="w-28 px-2 hidden md:block">UoM</div>`
      } else {
        columnHeaders = `
          <div class="w-8"></div>
          <div class="w-16 px-2">Item</div>
          <div class="w-20 px-2 hidden md:block">CSI Div</div>
          <div class="w-20 px-2 hidden md:block">Type</div>
          <div class="flex-1 min-w-0 px-2">Work</div>
          <div class="flex-1 min-w-0 px-2 hidden lg:block">Description</div>
          <div class="w-28 px-2 hidden md:block">UoM</div>`
      }

      rows = lineItems.map(li => this._renderSovLineItemRow(li, category.id)).join("")
    }

    const lineItemsSection = lineItems.length > 0 ? `
      <div>
        <div class="flex items-center px-3 py-1.5 bg-gray-50/50 text-[13px] font-medium text-gray-400 tracking-wider">
          ${columnHeaders}
          <div class="w-10"></div>
        </div>
        <div class="divide-y divide-gray-200" data-sov-category-id="${category.id}">
          ${rows}
        </div>
      </div>` : ""

    return `
      <div class="rounded-lg border border-gray-200" data-category-name="${nameDown}" data-sov-category-block>
        <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h4 class="text-[13px] font-semibold text-gray-500">${esc(category.name)}</h4>
          <div class="relative">
            <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                    data-action="click->cpanel-finances#toggleMenu">
              ${KEBAB_SVG}
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                      data-action="click->cpanel-finances#renameSovCategory"
                      data-id="${category.id}" data-name="${esc(category.name)}">
                Rename
              </button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      data-action="click->cpanel-finances#deleteSovCategory"
                      data-id="${category.id}">
                Remove
              </button>
            </div>
          </div>
        </div>
        ${lineItemsSection}
        <div class="px-4 py-2 border-t border-gray-100 bg-white text-right">
          <button type="button" class="text-[13px] text-brand hover:underline cursor-pointer"
                  data-action="click->cpanel-finances#openAddSovLineItem"
                  data-category-id="${category.id}">
            + Add Line Item
          </button>
        </div>
      </div>`
  }

  _renderSovLineItemRow(li, categoryId) {
    const esc = this._escHtml.bind(this)
    let dataCells = ""

    if (this.unitCompanyValue) {
      dataCells = `
        <div class="flex-1 min-w-0 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.description)}</span></div>
        <div class="flex-1 min-w-0 px-2 hidden md:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.definition)}</span></div>
        <div class="w-28 px-2 hidden md:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.unit_of_measurement)}</span></div>
        <div class="w-24 px-2 hidden lg:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.labor_mile)}</span></div>
        <div class="flex-1 min-w-0 px-2 hidden lg:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.est_quantities_formula)}</span></div>`
    } else if (this.descCompanyValue) {
      dataCells = `
        <div class="w-16 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.item)}</span></div>
        <div class="w-20 px-2 hidden md:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.csi_div)}</span></div>
        <div class="w-20 px-2 hidden md:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.line_item_type)}</span></div>
        <div class="flex-1 min-w-0 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.description)}</span></div>
        <div class="flex-1 min-w-0 px-2 hidden lg:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.definition)}</span></div>
        <div class="w-28 px-2 hidden md:block"><span class="text-[13px] text-text-muted truncate block">${esc(li.unit_of_measurement)}</span></div>`
    } else {
      dataCells = `
        <div class="w-16 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.item)}</span></div>
        <div class="w-20 px-2 hidden md:block"><span class="text-[13px] text-text-body truncate block">${esc(li.csi_div)}</span></div>
        <div class="w-20 px-2 hidden md:block"><span class="text-[13px] text-text-body truncate block">${esc(li.line_item_type)}</span></div>
        <div class="flex-1 min-w-0 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.description)}</span></div>
        <div class="flex-1 min-w-0 px-2 hidden lg:block"><span class="text-[13px] text-text-body truncate block">${esc(li.definition)}</span></div>
        <div class="w-28 px-2 hidden md:block"><span class="text-[13px] text-text-body truncate block">${esc(li.unit_of_measurement)}</span></div>`
    }

    return `
      <div class="flex items-center px-3 py-1.5 bg-white hover:bg-gray-50 transition-colors"
           data-line-item-id="${li.id}" data-category-id="${categoryId}">
        <div class="w-8 flex-shrink-0 drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
          ${DRAG_SVG}
        </div>
        ${dataCells}
        <div class="w-10 flex-shrink-0">
          <div class="relative">
            <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                    data-action="click->cpanel-finances#toggleMenu">
              ${KEBAB_SVG}
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                      data-action="click->cpanel-finances#editSovLineItem"
                      data-id="${li.id}"
                      data-item="${esc(li.item)}"
                      data-csi-div="${esc(li.csi_div)}"
                      data-type="${esc(li.line_item_type)}"
                      data-description="${esc(li.description)}"
                      data-definition="${esc(li.definition)}"
                      data-uom="${esc(li.unit_of_measurement)}"
                      data-labor-mile="${esc(li.labor_mile)}"
                      data-est-quantity-formula="${esc(li.est_quantities_formula)}">
                Edit
              </button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      data-action="click->cpanel-finances#deleteSovLineItem"
                      data-id="${li.id}">
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>`
  }

  // ── Client-Side Rendering: Fee Schedule ───────────────────────────

  async _loadFeeCategories() {
    if (!this.hasFeeCategoriesContainerTarget) return
    try {
      const resp = await fetch(this._financesCollectionUrl("fee_schedule_data"), { headers: this._headers() })
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`)
      const { categories } = await resp.json()
      this.feeCategoriesContainerTarget.innerHTML = categories.map(c => this._renderFeeCategory(c)).join("")
      this._initSortables()
    } catch {
      this._showToast("Failed to refresh list", "error")
    }
  }

  _renderFeeCategory(category) {
    const esc = this._escHtml.bind(this)
    const nameDown = esc((category.name || "").toLowerCase())
    const lineItems = category.line_items || []

    let lineItemsSection = ""
    if (lineItems.length > 0) {
      const rows = lineItems.map(li => this._renderFeeLineItemRow(li, category.id)).join("")
      lineItemsSection = `
        <div>
          <div class="flex items-center px-3 py-1.5 bg-gray-50/50 text-[13px] font-medium text-gray-400 tracking-wider">
            <div class="w-8"></div>
            <div class="w-24 px-2">Item</div>
            <div class="flex-1 min-w-0 px-2">Work</div>
            <div class="w-10"></div>
          </div>
          <div class="divide-y divide-gray-200" data-fee-category-id="${category.id}">
            ${rows}
          </div>
        </div>`
    }

    return `
      <div class="rounded-lg border border-gray-200" data-category-name="${nameDown}" data-fee-category-block>
        <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h4 class="text-[13px] font-semibold text-gray-500">${esc(category.name)}</h4>
          <div class="relative">
            <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                    data-action="click->cpanel-finances#toggleMenu">
              ${KEBAB_SVG}
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                      data-action="click->cpanel-finances#renameFeeCategory"
                      data-id="${category.id}" data-name="${esc(category.name)}">
                Rename
              </button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      data-action="click->cpanel-finances#deleteFeeCategory"
                      data-id="${category.id}">
                Remove
              </button>
            </div>
          </div>
        </div>
        ${lineItemsSection}
        <div class="px-4 py-2 border-t border-gray-100 bg-white text-right">
          <button type="button" class="text-[13px] text-brand hover:underline cursor-pointer"
                  data-action="click->cpanel-finances#openAddFeeLineItem"
                  data-category-id="${category.id}">
            + Add Line Item
          </button>
        </div>
      </div>`
  }

  _renderFeeLineItemRow(li, categoryId) {
    const esc = this._escHtml.bind(this)

    return `
      <div class="flex items-center px-3 py-1.5 bg-white hover:bg-gray-50 transition-colors"
           data-line-item-id="${li.id}" data-category-id="${categoryId}">
        <div class="w-8 flex-shrink-0 drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
          ${DRAG_SVG}
        </div>
        <div class="w-24 px-2"><span class="text-[13px] font-medium text-text-body truncate block">${esc(li.item)}</span></div>
        <div class="flex-1 min-w-0 px-2"><span class="text-[13px] text-text-body truncate block">${esc(li.work)}</span></div>
        <div class="w-10 flex-shrink-0">
          <div class="relative">
            <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                    data-action="click->cpanel-finances#toggleMenu">
              ${KEBAB_SVG}
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                      data-action="click->cpanel-finances#editFeeLineItem"
                      data-id="${li.id}" data-item="${esc(li.item)}" data-work="${esc(li.work)}">
                Edit
              </button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      data-action="click->cpanel-finances#deleteFeeLineItem"
                      data-id="${li.id}">
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>`
  }

  // ── Client-Side Rendering: Retention ──────────────────────────────

  async _loadRetentionData() {
    if (!this.hasRetentionSectionTarget) return
    try {
      const resp = await fetch(this._financesCollectionUrl("retention_data"), { headers: this._headers() })
      const data = await resp.json()
      this._renderRetentionSection(data)
      // Sync hasBelowSov value so the Add modal hides/shows the option correctly
      if (data.has_below_sov_amount !== undefined) {
        this.hasBelowSovValue = data.has_below_sov_amount
      }
    } catch {
      // Silently fail — toast was already shown for the action
    }
  }

  _renderRetentionSection(data) {
    const esc = this._escHtml.bind(this)
    const pct = esc(String(data.default_percentage))
    const KEBAB = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>'
    const KEBAB_SM = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>'

    const rulesHtml = (data.rules || []).map((rule, i) => {
      let ruleLabel = ""
      if (rule.rule_type === "below_sov_amount") {
        ruleLabel = `Below SOV Amount (${this._formatCurrency(rule.below_sov_amount)})`
      } else if (rule.rule_type === "default_state") {
        ruleLabel = `${esc(rule.state_display || rule.state)} (State Default)`
      }
      const rowBg = i % 2 ? "bg-gray-50/60" : "bg-white"
      return `
          <tr data-id="${rule.id}" class="${rowBg} border-b border-gray-100 hover:bg-brand/[0.04]">
            <td class="px-3 py-1.5 text-[13px] text-gray-900 font-medium">${ruleLabel}</td>
            <td class="px-3 py-1.5 text-[13px] text-gray-500 text-right">${esc(String(rule.retention_percentage))}%</td>
            <td class="px-3 py-1.5 text-right">
              <div class="relative" data-controller="dropdown">
                <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
                  ${KEBAB}
                </button>
                <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-32" data-dropdown-target="menu">
                  <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                          data-action="click->cpanel-finances#editExceptionRule" data-id="${rule.id}">Edit</button>
                  <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                          data-action="click->cpanel-finances#deleteExceptionRule" data-id="${rule.id}">Delete</button>
                </div>
              </div>
            </td>
          </tr>`
    }).join("")

    const PLUS = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>'

    this.retentionSectionTarget.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-1">
          <span class="text-[13px] font-semibold text-gray-500">General Default - ${pct}%</span>
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer"
                  data-action="click->cpanel-finances#editDefaultRetention">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>
          </button>
        </div>
        <button type="button"
                class="bg-brand hover:bg-brand-dark active:bg-brand-700 text-white flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg whitespace-nowrap cursor-pointer transition-colors"
                data-action="click->cpanel-finances#openAddExceptionRule">
          ${PLUS}
          <span class="hidden xl:inline">Add Rule</span>
        </button>
      </div>
      <div class="rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead>
            <tr class="bg-gray-50 text-left border-b border-gray-200">
              <th class="px-3 py-2 font-semibold text-[13px] text-gray-500">Rule</th>
              <th class="px-3 py-2 font-semibold text-[13px] text-gray-500 text-right">Retention %</th>
              <th class="px-3 py-2 font-semibold text-[13px] text-gray-500 w-10"></th>
            </tr>
          </thead>
          <tbody>
            ${rulesHtml}
          </tbody>
        </table>
      </div>`

    // Update the modal input to match new default
    if (this.hasDefaultRetentionInputTarget) {
      this.defaultRetentionInputTarget.value = data.default_percentage
    }
  }

  // ── Utilities ───────────────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      event.currentTarget.classList.add("hidden")
    }
  }

  _financesCollectionUrl(action) {
    const basePath = window.location.pathname.replace(/\/finances.*/, "/finances")
    return `${basePath}/${action}`
  }

  _clearSovLineItemForm() {
    if (this.hasSovItemTarget) this.sovItemTarget.value = ""
    if (this.hasSovCsiDivTarget) this.sovCsiDivTarget.value = ""
    if (this.hasSovTypeTarget) this.sovTypeTarget.value = ""
    if (this.hasSovDescriptionTarget) this.sovDescriptionTarget.value = ""
    if (this.hasSovDefinitionTarget) this.sovDefinitionTarget.value = ""
    if (this.hasSovUomTarget) this.sovUomTarget.value = ""
    if (this.hasSovLaborMileTarget) this.sovLaborMileTarget.value = ""
    if (this.hasSovEstFormulaTarget) this.sovEstFormulaTarget.value = ""
  }

  _escHtml(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _formatCurrency(amount) {
    if (amount == null || amount === "") return "$0.00"
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
  }
}
